// =============================================================================
// SalesScreen — Bokun-style sales feed
// =============================================================================
// Booking-centric list. Each row uses a left-side colour bar:
//   green  = online   (widget-created, no operator)
//   red    = offline  (walk-in / phone, created by an operator)
// Top line: booking number + booked-at + customer + source + total + payment.
// Bottom line: experience + first slot + party + room + lifecycle pill.
// Click the booking number to open BookingDetailScreen.
//
// Filters along the top mirror Bokun: search, lifecycle multi-select, source
// dropdown, booked date range, travelled date range. Default lifecycle filter
// = confirmed + arrived (most useful day-to-day).
// =============================================================================
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listSales,
  type SalesRow,
  type SalesSource,
} from '../lib/sales';
import { formatError } from '../lib/errors';
import { formatVenueDateTime, formatVenueDate } from '../lib/time';
import { useIsSingleRoomByRoomId } from '../lib/room-counts';
import { useLocationFilter } from '../lib/location-context';
import { NewBookingModal } from '../components/NewBookingModal';
import { resendVoucherEmail } from '../api/resend-voucher-email';

const PAGE_SIZE = 50;

const LIFECYCLE_OPTIONS = [
  { value: 'confirmed', label: 'Bevestigd' },
  { value: 'arrived',   label: 'Aangekomen' },
  { value: 'no_show',   label: 'No-show' },
  { value: 'cancelled', label: 'Geannuleerd' },
  { value: 'draft',     label: 'Concept' },
] as const;

const LIFECYCLE_LABEL: Record<string, string> = {
  draft:     'Concept',
  confirmed: 'Bevestigd',
  arrived:   'Aangekomen',
  no_show:   'No-show',
  cancelled: 'Geannuleerd',
};

const PAYMENT_LABEL: Record<string, string> = {
  none:     'Geen',
  pending:  'In behandeling',
  paid:     'Betaald',
  refunded: 'Terugbetaald',
  failed:   'Mislukt',
  unpaid:   'Nog niet betaald',
};

export function SalesScreen() {
  const [rows, setRows] = useState<SalesRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [lifecycleSet, setLifecycleSet] = useState<Set<string>>(
    () => new Set(['confirmed', 'arrived']),
  );
  const [source, setSource] = useState<SalesSource | ''>('');
  const [bookedFrom, setBookedFrom] = useState('');
  const [bookedTo, setBookedTo] = useState('');
  const [travelledFrom, setTravelledFrom] = useState('');
  const [travelledTo, setTravelledTo] = useState('');
  const [page, setPage] = useState(0);

  const [showNewBooking, setShowNewBooking] = useState(false);
  const isSingleRoom = useIsSingleRoomByRoomId();
  const { locationId } = useLocationFilter();

  const load = useCallback(async () => {
    setError(null);
    try {
      const lifecycleArr =
        lifecycleSet.size === 0 ? null : Array.from(lifecycleSet);
      const result = await listSales({
        search:           search || null,
        lifecycleStatuses: lifecycleArr,
        source:           source || null,
        bookedFrom:       bookedFrom || null,
        bookedTo:         bookedTo || null,
        travelledFrom:    travelledFrom || null,
        travelledTo:      travelledTo || null,
        locationId,
        limit:            PAGE_SIZE,
        offset:           page * PAGE_SIZE,
      });
      setRows(result.rows);
      setTotal(result.total);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [search, lifecycleSet, source, bookedFrom, bookedTo, travelledFrom, travelledTo, locationId, page]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  // Reset to page 0 whenever a filter changes.
  useEffect(() => {
    setPage(0);
  }, [search, lifecycleSet, source, bookedFrom, bookedTo, travelledFrom, travelledTo, locationId]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, (page + 1) * PAGE_SIZE);

  const toggleLifecycle = (v: string) => {
    setLifecycleSet((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  };

  return (
    <div className="screen sales-screen">
      <div className="sales-header">
        <h1 className="payments-title">Sales feed</h1>
        <div className="sales-header-actions">
          <button
            className="walkin-primary-btn"
            onClick={() => setShowNewBooking(true)}
          >
            + Nieuwe boeking
          </button>
        </div>
      </div>

      <div className="payments-filters">
        <form
          className="walkin-field payments-filter sales-search"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchInput.trim());
          }}
        >
          <span>Zoek (boekingsnr / naam / email / telefoon)</span>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Druk op Enter"
          />
        </form>

        <label className="walkin-field payments-filter">
          <span>Bron</span>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as SalesSource | '')}
          >
            <option value="">Alle</option>
            <option value="online">Online (widget)</option>
            <option value="offline">Offline (walk-in / tel.)</option>
          </select>
        </label>

        <label className="walkin-field payments-filter">
          <span>Geboekt vanaf</span>
          <input
            type="date"
            value={bookedFrom}
            onChange={(e) => setBookedFrom(e.target.value)}
          />
        </label>
        <label className="walkin-field payments-filter">
          <span>Geboekt t/m</span>
          <input
            type="date"
            value={bookedTo}
            onChange={(e) => setBookedTo(e.target.value)}
          />
        </label>

        <label className="walkin-field payments-filter">
          <span>Slot vanaf</span>
          <input
            type="date"
            value={travelledFrom}
            onChange={(e) => setTravelledFrom(e.target.value)}
          />
        </label>
        <label className="walkin-field payments-filter">
          <span>Slot t/m</span>
          <input
            type="date"
            value={travelledTo}
            onChange={(e) => setTravelledTo(e.target.value)}
          />
        </label>

        <fieldset className="sales-lifecycle-pills">
          <legend>Status</legend>
          {LIFECYCLE_OPTIONS.map((o) => (
            <label key={o.value} className="sales-pill-toggle">
              <input
                type="checkbox"
                checked={lifecycleSet.has(o.value)}
                onChange={() => toggleLifecycle(o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </fieldset>
      </div>

      <div className="payments-summary muted">
        {total === 0
          ? 'Geen resultaten.'
          : `Resultaten ${showingFrom} – ${showingTo} van ${total}`}
      </div>

      {error ? <div className="error-card">{error}</div> : null}
      {loading ? <div className="muted">Laden…</div> : null}

      {!loading && rows.length > 0 ? (
        <ul className="sales-list">
          {rows.map((r) => (
            <SalesItem
              key={r.id}
              row={r}
              showRoom={
                r.first_room_id ? !isSingleRoom(r.first_room_id) : true
              }
            />
          ))}
        </ul>
      ) : null}

      {totalPages > 1 ? (
        <div className="payments-pagination">
          <button
            className="btn-link"
            disabled={page <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ‹ Vorige
          </button>
          <span className="muted">
            Pagina {page + 1} / {totalPages}
          </span>
          <button
            className="btn-link"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Volgende ›
          </button>
        </div>
      ) : null}

      {showNewBooking ? (
        <NewBookingModal
          onClose={(created) => {
            setShowNewBooking(false);
            if (created) void load();
          }}
        />
      ) : null}
    </div>
  );
}

function SalesItem({
  row,
  showRoom,
}: {
  row: SalesRow;
  showRoom: boolean;
}) {
  const isVoucher = row.kind === 'voucher';
  const isGiftCard = row.kind === 'gift_card';
  // Voucher rows: staff can re-send the gift-voucher email (covers a failed
  // post-payment send or a customer who lost the mail). row.id is the
  // voucher_purchases id for kind='voucher' rows.
  const [voucherResendBusy, setVoucherResendBusy] = useState(false);
  const [voucherResendNotice, setVoucherResendNotice] = useState<{
    kind: 'ok' | 'err';
    message: string;
  } | null>(null);
  const handleVoucherResend = useCallback(async () => {
    setVoucherResendBusy(true);
    setVoucherResendNotice(null);
    const result = await resendVoucherEmail({ voucherPurchaseId: row.id });
    setVoucherResendBusy(false);
    if (!result.ok || !result.data) {
      const msg = result.error?.message ?? result.error?.error ?? 'onbekende fout';
      setVoucherResendNotice({ kind: 'err', message: `Mislukt: ${msg}` });
      return;
    }
    setVoucherResendNotice({ kind: 'ok', message: 'Verstuurd ✓' });
  }, [row.id]);

  const fullName =
    row.guest_name?.trim() ||
    [row.first_name, row.last_name].filter(Boolean).join(' ') ||
    '—';
  const sourceLabel =
    row.source === 'online'
      ? 'Online'
      : `Offline${row.operator_email ? ` (${row.operator_email})` : ''}`;
  const totalEur =
    row.total_cents != null ? `€ ${(row.total_cents / 100).toFixed(2)}` : '—';

  const paymentClass =
    row.payment_status === 'paid'
      ? 'voucher-status-ok'
      : row.payment_status === 'refunded'
        ? 'voucher-status-warn'
        : row.payment_status === 'failed'
          ? 'voucher-status-off'
          : 'voucher-status-off';

  const lifecycleClass =
    row.lifecycle_status === 'arrived'
      ? 'voucher-status-ok'
      : row.lifecycle_status === 'cancelled' || row.lifecycle_status === 'no_show'
        ? 'voucher-status-off'
        : 'voucher-status-warn';

  return (
    <li className={`sales-card sales-card-${row.source}`}>
      <div className="sales-card-bar" />
      <div className="sales-card-body">
        <div className="sales-card-row sales-card-row-top">
          <div className="sales-card-cell sales-card-ref">
            {isGiftCard ? (
              <span className="voucher-code">
                <Link to={`/cadeaubon/${row.booking_number.replace(/-/g, '')}`} className="voucher-code">
                  {row.booking_number}
                </Link>{' '}
                <span className="voucher-pill voucher-status-warn">Cadeaubon (kaart)</span>
              </span>
            ) : isVoucher ? (
              // Voucher sales aren't bookings — link to the voucher detail
              // (history, redemptions) plus a badge so staff can tell them apart.
              <span className="voucher-code">
                <Link to={`/voucher/${row.booking_number}`} className="voucher-code">
                  {row.booking_number}
                </Link>{' '}
                <span className="voucher-pill voucher-status-warn">Cadeaubon</span>
              </span>
            ) : (
              <Link to={`/booking/${row.booking_number}`} className="voucher-code">
                {row.booking_number}
              </Link>
            )}
            <div className="muted sales-card-sub">
              {formatVenueDateTime(row.created_at)}
            </div>
          </div>

          <div className="sales-card-cell sales-card-customer">
            <div>{fullName}</div>
            <div className="muted sales-card-sub">
              {row.customer_email ? (
                <a
                  href={`mailto:${row.customer_email}`}
                  className="sales-card-link"
                >
                  {row.customer_email}
                </a>
              ) : null}
              {row.customer_email && row.customer_phone ? ' · ' : ''}
              {row.customer_phone ? (
                <a
                  href={`tel:${row.customer_phone.replace(/\s+/g, '')}`}
                  className="sales-card-link"
                >
                  {row.customer_phone}
                </a>
              ) : null}
            </div>
          </div>

          <div className="sales-card-cell sales-card-source">
            <div>{sourceLabel}</div>
          </div>

          <div className="sales-card-cell sales-card-total">
            <div className="sales-card-total-amount">{totalEur}</div>
            <span className={`voucher-pill ${paymentClass}`}>
              {PAYMENT_LABEL[row.payment_status] ?? row.payment_status}
            </span>
          </div>
        </div>

        <div className="sales-card-row sales-card-row-bottom">
          <div className="sales-card-cell sales-card-experience">
            {row.experience_name ?? '—'}
            {showRoom && row.first_room_name ? (
              <span className="muted"> · {row.first_room_name}</span>
            ) : null}
            <span className="muted"> · {row.party_size}p</span>
            {row.slots_count > 1 ? (
              <span className="muted"> · gesplitst ({row.slots_count}×)</span>
            ) : null}
            {row.discount ? (
              <span className="sales-card-discount">
                {' · '}
                <strong>–€{(row.discount.amount_off_cents / 100).toFixed(2)}</strong>
                {' '}({row.discount.code})
              </span>
            ) : null}
          </div>

          <div className="sales-card-cell sales-card-slot muted">
            {row.first_slot_start
              ? `${formatVenueDate(row.first_slot_start)} ${formatVenueDateTime(
                  row.first_slot_start,
                ).split(', ').pop()}`
              : '—'}
          </div>

          <div className="sales-card-cell sales-card-lifecycle">
            {isVoucher && row.payment_status === 'paid' ? (
              <>
                <button
                  className="btn-link"
                  onClick={() => void handleVoucherResend()}
                  disabled={voucherResendBusy}
                  title="Verstuur de cadeaubon-mail (code + geldigheid) opnieuw naar de koper"
                >
                  {voucherResendBusy ? 'Versturen…' : 'Bon opnieuw mailen'}
                </button>
                {voucherResendNotice ? (
                  <span
                    className={
                      voucherResendNotice.kind === 'ok'
                        ? 'resend-notice resend-notice-ok'
                        : 'resend-notice resend-notice-err'
                    }
                  >
                    {voucherResendNotice.message}
                  </span>
                ) : null}{' '}
              </>
            ) : null}
            <span className={`voucher-pill ${lifecycleClass}`}>
              {LIFECYCLE_LABEL[row.lifecycle_status] ?? row.lifecycle_status}
            </span>
          </div>
        </div>
      </div>
    </li>
  );
}
